import {
  Box,
  Checkbox,
  CheckboxProps,
  Container,
  Form,
  FormField,
  Header,
  Input,
  InputProps,
  NonCancelableCustomEvent,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from '@amzn/awsui-components-core';
import React, { useState } from 'react';
import * as UtilsApi from '../../api/UtilsApi';
import '../../resources/styles/scss/components/apgl-user-profile/apgl-user-profile-form.css';
import { AppContextData } from '../../contexts/AppContext';
import {
  ExpertiseItem,
  ExpertiseType,
  FormErrorTextMap,
  FormErrorType,
  OtherPublicationItem,
  PublicationItem,
  PublicationType,
} from './APGLUserProfileUtils';
import { awsServiceDisplayNames } from '../utils/DisplayNames';
import {
  AWSServiceOptions,
  TechDomainOptions,
  YearsOfExperienceOptions,
} from '../utils/SelectOptions';
import { awsServiceLimit, techDomainLimit } from '../../api/UtilsApi';
import { PublicationList } from './PublicationList';
import {
  AWSServiceExperience,
  ExternalPublicationLink,
  TechDomainExperience,
  TechDomainDisplayNames,
} from '@amzn/awscat-pattern-library-service-model';
import { OtherPublicationList } from './OtherPublicationList';
import { ExpertiseList } from './ExpertiseList';

interface Props {
  publicationNameMap: Map<string, string>;
  reviewNameMap: Map<string, string>;
  upDescription: string;
  upIsPeerReviewer: boolean;
  upTechDomains: TechDomainExperience[];
  upAwsServices: AWSServiceExperience[];
  apgPublications: string[];
  reviewedPublications: string[];
  otherPublications: ExternalPublicationLink[];
  allPubs: string[];
  allReviews: string[];
  appContext: AppContextData;
  handleDescriptionChanged: (description: string) => void;
  handlePeerReviewerOptionChanged: (isPeerReviewer: boolean) => void;
  handleTechDomainsChanged: (items: ExpertiseItem[]) => void;
  handleAWSServicesChanged: (items: ExpertiseItem[]) => void;
  handleAuthoredPubsChanged: (pubs: PublicationItem[]) => void;
  handleReviewedPubsChanged: (pubs: PublicationItem[]) => void;
  handleOtherPubsChanged: (pubs: OtherPublicationItem[]) => void;
  handleFormErrorChanged: (error: Map<FormErrorType, string>) => void;
  initFormError: Map<FormErrorType, string>;
}

const attributeExists = (attribute: any[]): boolean => {
  return Array.isArray(attribute) && attribute.length > 0;
};

const initContents = (cids: string[]): PublicationItem[] => {
  let id = -1;
  const pubItems = cids.map((cid) => {
    return { contentId: cid, id: ++id };
  });
  return pubItems;
};

// ID for tracking attributes in form
const initLastId = (attribute: any[]): number => {
  return attributeExists(attribute) ? attribute.length - 1 : 0;
};

const APGLUserProfileFormBody: React.FunctionComponent<Props> = ({
  publicationNameMap,
  reviewNameMap,
  upDescription,
  upIsPeerReviewer,
  upTechDomains,
  upAwsServices,
  apgPublications,
  reviewedPublications,
  otherPublications,
  allPubs,
  allReviews,
  appContext,
  handleDescriptionChanged,
  handlePeerReviewerOptionChanged,
  handleTechDomainsChanged,
  handleAWSServicesChanged,
  handleAuthoredPubsChanged,
  handleReviewedPubsChanged,
  handleOtherPubsChanged,
  handleFormErrorChanged,
  initFormError,
}: Props) => {
  const { authnInfo } = appContext;
  // @ts-expect-error TS2339 - strict migration
  const { userProfile } = authnInfo;

  /* states */

  // description states
  const [description, setDescription] = useState<string>(upDescription);

  // peer reviewer option states
  const [isPeerReviewer, setIsPeerReviewer] = useState<boolean>(upIsPeerReviewer);

  // expertise states
  // @ts-expect-error TS2345 - strict migration
  const [techDomains, setTechDomains] = useState<ExpertiseItem[]>(() => {
    if (attributeExists(upTechDomains)) {
      let id = -1;
      return upTechDomains.map((domain) => {
        return {
          domainExpertise: domain.techDomain,
          yearsOfExperience: domain.years,
          id: ++id,
          rank: domain.rank,
        };
      });
    } else {
      return [{ domainExpertise: null, yearsOfExperience: null, id: 0, rank: 0 }];
    }
  });
  // @ts-expect-error TS2345 - strict migration
  const [awsServices, setAwsServices] = useState<ExpertiseItem[]>(() => {
    if (attributeExists(upAwsServices)) {
      let id = -1;
      return upAwsServices.map((service) => {
        return {
          domainExpertise: service.awsService,
          yearsOfExperience: service.years,
          id: ++id,
          rank: service.rank,
        };
      });
    } else {
      return [{ domainExpertise: null, yearsOfExperience: null, id: 0, rank: 0 }];
    }
  });

  // to track increasing id
  const [techDomainLastId, setTechDomainLastId] = useState<number>(initLastId(upTechDomains));
  const [awsServiceLastId, setAwsServiceLastId] = useState<number>(initLastId(upAwsServices));

  // authored content states
  const [authoredPubs, setAuthoredPubs] = useState<PublicationItem[]>(
    attributeExists(apgPublications) ? initContents(apgPublications) : [],
  ); // selected authored publications
  const [pubLastId, setPubLastId] = useState<number>(initLastId(apgPublications));

  // reviewed content states
  const [reviewedPubs, setReviewedPubs] = useState<PublicationItem[]>(
    attributeExists(reviewedPublications) ? initContents(reviewedPublications) : [],
  ); // selected reviewed publications
  const [reviewLastId, setReviewLastId] = useState<number>(initLastId(reviewedPublications));

  // other publication states
  const [otherPubs, setOtherPubs] = useState<OtherPublicationItem[]>(() => {
    if (attributeExists(otherPublications)) {
      let id = -1;
      const pubItems = otherPublications.map((pub) => {
        return { title: pub.title, url: pub.url, id: ++id };
      });
      return pubItems;
    } else {
      return [{ title: '', url: '', id: 0 }];
    }
  });
  const [otherPubLastId, setOtherPubLastId] = useState<number>(initLastId(otherPublications));

  // validation error states
  const [formError, setFormError] = useState<Map<FormErrorType, string>>(initFormError);

  /* helper functions */
  const updateAttributeDuplicationError = (map: Map<string, number>, type: FormErrorType) => {
    if (
      type !== FormErrorType.DomainDuplicationError &&
      type !== FormErrorType.ServiceDuplicationError &&
      type !== FormErrorType.PubDuplicationError &&
      type !== FormErrorType.ReviewDuplicationError
    ) {
      throw new Error(`Invalid duplication error type: ${type}`);
    }
    let hasError = false;
    for (const [attribute, count] of map.entries()) {
      if (count > 1) {
        hasError = true;
        break;
      }
    }
    if (hasError) {
      // @ts-expect-error TS2345 - strict migration
      formError.set(type, FormErrorTextMap.get(type));
    } else {
      formError.delete(type);
    }
    updateFormError(formError);
  };

  const updateAttributeInputError = (inputError: Map<number, boolean>, type: FormErrorType) => {
    if (
      type !== FormErrorType.DomainInputError &&
      type !== FormErrorType.ServiceInputError &&
      type !== FormErrorType.OtherPubInputError
    ) {
      throw new Error(`Invalid input error type ${type}`);
    }
    let hasError = false;
    for (const [id, containsError] of inputError.entries()) {
      if (containsError) {
        hasError = true;
        break;
      }
    }
    if (hasError) {
      // @ts-expect-error TS2345 - strict migration
      formError.set(type, FormErrorTextMap.get(type));
    } else {
      formError.delete(type);
    }
    updateFormError(formError);
  };

  /* about section */
  const aboutYouHeader = <Header variant="h3">About you</Header>;

  const name = `${userProfile.name} (${userProfile.type})`;

  const onDescriptionChanged = (event: NonCancelableCustomEvent<InputProps.ChangeDetail>) => {
    const descriptionText = event.detail.value;
    const descriptionFieldError = !descriptionText
      ? 'Description field cannot be empty.'
      : descriptionText.length > UtilsApi.descriptionInputMaxLength
        ? `Description must be less than ${UtilsApi.descriptionInputMaxLength} characters.`
        : '';
    if (descriptionFieldError) {
      formError.set(FormErrorType.DescriptionError, descriptionFieldError);
    } else {
      formError.delete(FormErrorType.DescriptionError);
    }
    setDescription(descriptionText);
    handleDescriptionChanged(descriptionText);
    updateFormError(formError);
  };

  const charactersRemaining = UtilsApi.descriptionInputMaxLength - description.length;
  const descriptionMessage =
    description.length > UtilsApi.descriptionInputMaxLength ? (
      <div>
        <StatusIndicator type="warning" colorOverride="red">
          <Box fontSize="body-s" display="inline" color="text-status-error">
            {`Must be less than ${UtilsApi.descriptionInputMaxLength} characters`}
          </Box>
        </StatusIndicator>
      </div>
    ) : (
      <Box variant="p" color="text-body-secondary" fontSize="body-s">
        {`${charactersRemaining} character${charactersRemaining > 1 ? 's' : ''} remaining`}
      </Box>
    );

  const onPeerReviewerCheckBoxChanged = (
    event: NonCancelableCustomEvent<CheckboxProps.ChangeDetail>,
  ) => {
    const newIsPeerReviewer = event.detail.checked;
    setIsPeerReviewer(newIsPeerReviewer);
    handlePeerReviewerOptionChanged(newIsPeerReviewer);
  };

  const peerReviewerCheckBox = (
    <Checkbox onChange={onPeerReviewerCheckBoxChanged} checked={isPeerReviewer}>
      I want to be an APGL peer reviewer
    </Checkbox>
  );

  const aboutSection = (
    <Container header={aboutYouHeader}>
      <SpaceBetween direction="vertical" size="s">
        <FormField label="Full name">
          <Input value={name} disabled ariaRequired />
        </FormField>
        <FormField
          label="What should others know about you?"
          description="Provide any pertinent information such as certification, associations, or projects that add value to your areas of expertise."
        >
          <Textarea value={description} onChange={onDescriptionChanged} ariaRequired />
          {descriptionMessage}
        </FormField>
        {peerReviewerCheckBox}
      </SpaceBetween>
    </Container>
  );

  /* domain expertise section */
  const expertiseHeader = <Header variant="h3">Domain expertise</Header>;

  const onDomainExperiencesChanged = (experiences: ExpertiseItem[]) => {
    setTechDomains(experiences);
    handleTechDomainsChanged(experiences);
  };
  const onServiceExperiencesChanged = (experiences: ExpertiseItem[]) => {
    setAwsServices(experiences);
    handleAWSServicesChanged(experiences);
  };

  const updateTechDomainLastId = (id: number) => {
    setTechDomainLastId(id);
  };

  const updateAwsServiceLastId = (id: number) => {
    setAwsServiceLastId(id);
  };

  const expertiseSection = (
    <Container header={expertiseHeader}>
      <SpaceBetween direction="vertical" size="s">
        <Box>
          <ExpertiseList
            expertiseType={ExpertiseType.TechDomain}
            items={techDomains}
            lastId={techDomainLastId}
            expertiseNameMap={TechDomainDisplayNames}
            expertiseOptions={TechDomainOptions}
            yearsOptions={YearsOfExperienceOptions}
            placeholder={'Select domain'}
            maxCount={techDomainLimit}
            handleExpertiseItemsChanged={onDomainExperiencesChanged}
            handleExpertiseLastIdUpdated={updateTechDomainLastId}
            handleCountMapUpdated={updateAttributeDuplicationError}
            handleInputErrorUpdated={updateAttributeInputError}
          />
        </Box>
        <Box>
          <ExpertiseList
            expertiseType={ExpertiseType.AWSService}
            items={awsServices}
            lastId={awsServiceLastId}
            expertiseNameMap={awsServiceDisplayNames}
            expertiseOptions={AWSServiceOptions}
            yearsOptions={YearsOfExperienceOptions}
            placeholder={'Select service'}
            maxCount={awsServiceLimit}
            handleExpertiseItemsChanged={onServiceExperiencesChanged}
            handleExpertiseLastIdUpdated={updateAwsServiceLastId}
            handleCountMapUpdated={updateAttributeDuplicationError}
            handleInputErrorUpdated={updateAttributeInputError}
          />
        </Box>
      </SpaceBetween>
    </Container>
  );

  /* publications section */
  const publicationHeader = <Header variant="h3">Your publications and reviews</Header>;

  const onPublicationsChanged = (pubs: PublicationItem[]) => {
    setAuthoredPubs(pubs);
    handleAuthoredPubsChanged(pubs);
  };

  const updatePublicationLastId = (id: number) => {
    setPubLastId(id);
  };

  const onReviewsChanged = (pubs: PublicationItem[]) => {
    setReviewedPubs(pubs);
    handleReviewedPubsChanged(pubs);
  };

  const updateReviewLastId = (id: number) => {
    setReviewLastId(id);
  };

  const onOtherPublicationsChanged = (pubs: OtherPublicationItem[]) => {
    setOtherPubs(pubs);
    handleOtherPubsChanged(pubs);
  };

  const updateOtherPublicationLastId = (id: number) => {
    setOtherPubLastId(id);
  };

  // for error check for the whole list
  const updateOtherPubDuplicationError = (
    titleMap: Map<string, number>,
    urlMap: Map<string, number>,
  ) => {
    let hasError = false;
    for (const [title, count] of titleMap.entries()) {
      if (count > 1) {
        hasError = true;
        break;
      }
    }
    if (!hasError) {
      for (const [url, count] of urlMap.entries()) {
        if (count > 1) {
          hasError = true;
          break;
        }
      }
    }
    if (hasError) {
      formError.set(
        FormErrorType.OtherPubDuplicationError,
        // @ts-expect-error TS2345 - strict migration
        FormErrorTextMap.get(FormErrorType.OtherPubDuplicationError),
      );
    } else {
      formError.delete(FormErrorType.OtherPubDuplicationError);
    }
    updateFormError(formError);
  };

  const authoredContentSection =
    Array.isArray(allPubs) && allPubs.length > 0 ? (
      <Box>
        <Box
          margin={{ bottom: 'm' }}
        >{`Choose published content you've authored (maximum ${UtilsApi.publicationLimit})`}</Box>
        <PublicationList
          contentNameMap={publicationNameMap}
          publicationType={PublicationType.AuthoredContent}
          selectedPubs={authoredPubs}
          allPubs={allPubs}
          lastId={pubLastId}
          handleSelectionsChanged={onPublicationsChanged}
          handleLastIdUpdated={updatePublicationLastId}
          handleCountMapUpdated={updateAttributeDuplicationError}
        />
      </Box>
    ) : (
      <Box>There is no published content you've authored found in APG Library.</Box>
    );

  const reviewedContentSection =
    Array.isArray(allReviews) && allReviews.length > 0 ? (
      <Box>
        <Box
          margin={{ bottom: 'm' }}
        >{`Choose published content you've reviewed (maximum ${UtilsApi.publicationLimit})`}</Box>
        <PublicationList
          contentNameMap={reviewNameMap}
          publicationType={PublicationType.ReviewedContent}
          selectedPubs={reviewedPubs}
          allPubs={allReviews}
          lastId={reviewLastId}
          handleSelectionsChanged={onReviewsChanged}
          handleLastIdUpdated={updateReviewLastId}
          handleCountMapUpdated={updateAttributeDuplicationError}
        />
      </Box>
    ) : (
      <Box>There is no published content you've reviewed found in APG Library.</Box>
    );

  const otherPublicationSection = (
    <Box>
      <OtherPublicationList
        items={otherPubs}
        lastId={otherPubLastId}
        handlePublicationsChanged={onOtherPublicationsChanged}
        handleLastIdUpdated={updateOtherPublicationLastId}
        handleCountMapUpdated={updateOtherPubDuplicationError}
        handleInputErrorUpdated={updateAttributeInputError}
      />
    </Box>
  );

  const publicationSection = (
    <Container header={publicationHeader}>
      <SpaceBetween direction="vertical" size="xl">
        <SpaceBetween direction="vertical" size="l">
          <Box variant="h3">AWS Prescriptive Guidance content</Box>
          {authoredContentSection}
          {reviewedContentSection}
        </SpaceBetween>
        <hr id="apgl-user-profile-divider" />
        {otherPublicationSection}
      </SpaceBetween>
    </Container>
  );

  const updateFormError = (error: Map<FormErrorType, string>) => {
    setFormError(error);
    handleFormErrorChanged(error);
  };

  return (
    <div>
      <SpaceBetween direction="vertical" size="xl">
        {aboutSection}
        {expertiseSection}
        {publicationSection}
      </SpaceBetween>
    </div>
  );
};

export default APGLUserProfileFormBody;
